<!-- src/views/management/sites-create.vue -->
<script setup lang="ts">
import { type as siteTypeList } from '@/datas/sites'
import { requiredValidator } from '@/@core/utils/validators'
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import { useSnackBar } from '@/utils/stores/snackbar'
import { onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: { layout: 'blank', action: 'write', subject: 'sites' },
})

const router = useRouter()
const route  = useRoute()
const snackbarStore = useSnackBar()

/* ── 상단 ‘관리’ 탭 라우팅 동기화 ───────────────────────── */
type MgmtTab = 'permissions' | 'users' | 'sites'
const mgmtTab = ref<MgmtTab>('sites')
function syncTabFromRoute() {
  const p = route.path || ''
  if (p.includes('/manage/permissions')) mgmtTab.value = 'permissions'
  else if (p.includes('/manage/users'))  mgmtTab.value = 'users'
  else                                       mgmtTab.value = 'sites'
}
onMounted(syncTabFromRoute)
watch(() => route.fullPath, syncTabFromRoute)
watch(mgmtTab, v => {
  const next =
    v === 'permissions' ? '/manage/permissions'
  : v === 'users'       ? '/manage/users'
  :                       '/manage/sites'
  if (route.path !== next) router.push(next)
})

/* ── 헤더 hover ───────────────────────────────────────── */
const hover = ref(false)

/* ── 폼 모델 ──────────────────────────────────────────── */
const data = reactive({
  name: '',
  phone: '',
  type: 'investigator',
  address: '',
  investigator: '',
})
const refForm = ref<any>(null)
const typeRadios = siteTypeList

const openPostCode = () => {
  new (window as any).daum.Postcode({
    oncomplete: (result: any) => { data.address = result.address },
  }).open()
}

const goBack = () => {
  sessionStorage.setItem('manage-sites-returning', '1')
  router.push('/manage/sites')
}

const saveSite = async () => {
  const validate = await refForm?.value?.validate()
  if (!validate?.valid) return

  await $api(`/management/sites`, {
    method: 'POST',
    body: { site: data },
  })

  snackbarStore.showSnackBar('기관이 등록되었습니다.')
  goBack()
}
</script>

<template>
  <!-- 상단 헤더 -->
  <VCard class="topbar-card" style="border-radius:0;">
  <VCardText class="d-flex justify-space-between align-between py-2">
    <!-- 👇 로고 버튼 (왼쪽) -->
    <div
      class="d-flex align-center cursor-pointer"
      @click="router.push('/projects')"
    >
      <img
        src="/images/home_logo.png"
        alt="홈"
        width="87"
        height="30"
        style="display:block"
      />
    </div>

    <!-- 오른쪽 기존 요소 -->
    <div class="d-flex align-center">
      <LogoutTime class="me-2" />
      <UserProfile />
      <div class="divider-vert" />
      <div
        class="mgmt-quick d-flex align-center px-2 py-1"
        :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
        @click="router.push('/manage/permissions')"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >
        <img src="/images/settings.png" alt="관리" class="icon-20 me-1" />
        <span class="text-gray-600">관리</span>
      </div>
    </div>
  </VCardText>
</VCard>

  <!-- 페이지 타이틀 -->
  <div class="page-title-wrap">
    <h2 class="page-title">관리</h2>
  </div>

  <!-- 관리 탭 -->
  <div class="mgmt-tabs">
    <VBtnToggle v-model="mgmtTab" class="tab-toggle" mandatory divided borderless>
      <VBtn :value="'permissions'" class="tab-btn">권한 관리</VBtn>
      <VBtn :value="'users'"       class="tab-btn">전체 사용자 관리</VBtn>
      <VBtn :value="'sites'"       class="tab-btn">전체 기관 관리</VBtn>
    </VBtnToggle>
  </div>

  <!-- 본문 -->
  <VRow class="page-wrap">
    <VCol cols="12" sm="12" md="10" lg="8" class="mx-auto">
      <VCard class="panel">
        <!-- 상단 섹션 타이틀 (굵은 타이틀) -->
        <div class="panel-title">기관 등록</div>

        <!-- 필드셋 타이틀 (얇은 바 + 설명) -->
        <div class="fieldset">
          <div class="fieldset-head">
            <span class="fieldset-title">기관 정보 입력</span>
          </div>

          <VForm ref="refForm" @submit.prevent>
            <!-- 기관명 -->
            <div class="form-row">
              <div class="form-label">기관명 <span class="req">*</span></div>
              <AppTextField v-model="data.name" :rules="[requiredValidator]" hide-details="auto" />
            </div>

            <!-- 연락처 -->
            <div class="form-row">
              <div class="form-label">연락처 <span class="req">*</span></div>
              <AppTextField v-model="data.phone" :rules="[requiredValidator]" hide-details="auto" />
            </div>

            <!-- 유형 -->
            <div class="form-row">
              <div class="form-label">유형 <span class="req">*</span></div>
              <div class="form-field">
                <VRadioGroup v-model="data.type" class="radio-row" inline>
                  <VRadio
                    v-for="(item, i) in typeRadios"
                    :key="i"
                    :value="item.value"
                    :label="item.text"
                    density="comfortable"
                  />
                </VRadioGroup>
              </div>
            </div>

            <!-- 주소 -->
            <div class="form-row">
              <div class="form-label">주소 <span class="req">*</span></div>
              <div class="field-with-append">
                <AppTextField v-model="data.address" :rules="[requiredValidator]" hide-details="auto">
                  <template #append>
                    <VBtn class="btn-lookup" variant="flat" color="#E9EFFD" @click="openPostCode">
                      <span class="lookup-text">검색</span>
                    </VBtn>
                  </template>
                </AppTextField>
              </div>
            </div>

            <!-- 담당자 -->
            <div class="form-row">
              <div class="form-label">대표자 <span class="req">*</span></div>
              <AppTextField v-model="data.investigator" :rules="[requiredValidator]" hide-details="auto" />
            </div>

            <!-- 액션 -->
            <div class="form-actions">
              <VBtn class="btn-primary" color="#3857F5" @click="saveSite">등록</VBtn>
              <VBtn class="btn-ghost"   color="secondary" variant="tonal" @click="goBack">취소</VBtn>
            </div>
          </VForm>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped>
/* ── 헤더 공통 ─────────────────────────────────────────── */
.divider-vert{ background-color:rgba(0,0,0,10%); block-size:30px; inline-size:1px; margin-inline:15px; }
.icon-20{ block-size:20px; inline-size:20px; }
.text-gray-600{ color:#4B5563; font-size:14px; }
.mgmt-quick{ border-radius:6px; cursor:pointer; transition:background-color .2s; }

/* ── 탭 공통 ───────────────────────────────────────────── */
.mgmt-tabs{ display:flex; justify-content:flex-start; padding-block:16px 0; padding-inline:34px; }
.tab-toggle{ border:1px solid #E8ECEF; border-radius:8px; background:#f4f6fa; box-shadow:0 3px 8px rgba(201,201,201,25%); }
.tab-btn{ min-inline-size:160px; }
.tab-btn.v-btn--active{ background:#eef0ff !important; }

/* ── 페이지 타이틀 ─────────────────────────────────────── */
.page-title-wrap{ padding-block: 50px 0 ; padding-inline: 34px; }
.page-title{ margin:0; border-block-end: 1px solid #DAE6EE; color:#111827; font-size:22px; font-weight:700; margin-block-end: 20px; padding-block-end: 20px; }

/* ── 본문/패널 ────────────────────────────────────────── */
.page-wrap{ margin-block:14px 28px; margin-inline:34px; }

.panel{
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 6%);
}

.panel-title{
  color:#111827; font-size:16px; font-weight:700;
  padding-block: 18px 0;
  padding-inline: 24px;
}

/* 필드셋: 상단 얇은 바 + 내부 여백 */
.fieldset{ padding-block: 14px 24px; padding-inline: 24px; }

.fieldset-head{
  border-block-end: 1px solid #EEF2F7;
  margin-block-end: 16px;
  padding-block: 12px 14px;
  padding-inline: 0;
}
.fieldset-title{ color:#5B6372; font-size:13px; font-weight:700; }

/* 폼 레이아웃 */
.form-row{
  display:grid; align-items:center; gap:12px;
  grid-template-columns: 180px 1fr; /* 라벨 폭 */
  margin-block: 10px;
}
.form-label{ color:#475569; font-size:14px; font-weight:600; }
.req{ color:#ef4444; }

/* 인풋 높이/모서리 톤 맞추기 */
:deep(.v-field){ border-radius: 10px; }
:deep(.v-field__input){ min-block-size: 42px; }

/* 라디오 한 줄 */
.radio-row :deep(.v-selection-control){ margin-inline-end: 20px; }

/* 주소 검색 버튼 스타일 */
.field-with-append :deep(.v-input__append) { padding-inline-start: 8px; }
.btn-lookup{ block-size: 36px; min-inline-size: 54px; }
.lookup-text{ color:#3857F5; font-weight:600; }

/* 액션 영역 */
.form-actions{
  display:flex; justify-content: center; gap: 12px;
  margin-block-start: 18px;
}
.btn-primary{ border-radius: 10px; block-size: 40px !important; min-inline-size: 120px; }
.btn-ghost  { border-radius: 10px; block-size: 40px !important; min-inline-size: 120px; }

@media (max-width: 720px){
  .form-row{ grid-template-columns: 1fr; }
}
</style>
